import { useEffect, useMemo, useState } from "react";
import { useDatasourcesClient } from "../shared/hooks";

/**
 * Keys of a generated OpenAPI `paths` interface that carry a POST operation (every Dynamic Data
 * API datasource is a POST). The filter is purely structural - the URL shapes themselves are owned
 * by the Storychart SDK, which has no version contract with this library.
 */
export type DatasourcePath<Paths> = keyof Paths & {
    [K in keyof Paths]: Paths[K] extends { post?: never } ? never : K;
}[keyof Paths] & string;

type PostOperation<Paths, P extends DatasourcePath<Paths>> = Paths[P] extends { post: infer O } ? O : never;

export type RequestOf<Paths, P extends DatasourcePath<Paths>> =
    PostOperation<Paths, P> extends { requestBody?: { content: { "application/json": infer B } } } ? B : Record<string, never>;

export type ResponseOf<Paths, P extends DatasourcePath<Paths>> =
    PostOperation<Paths, P> extends { responses: { 200: { content: { "application/json": infer R } } } } ? R : unknown;

export type DatasourceState<T> = {
    data: T | null;
    loading: boolean;
    error: unknown;
};

export type PluginApiOptions = {
    /**
     * Maps an OpenAPI document path to the endpoint passed to `PluginDataClient.fetchData`.
     * Defaults to stripping a leading "/api/", which is where the Storychart SDK currently mounts
     * the Dynamic Data API; override if a tenant's OpenAPI document is mounted differently.
     */
    pathToEndpoint?: (path: string) => string;
};

export type PluginApi<Paths> = {
    /**
     * Typed facade over the host app's DatasourcesClient. The request/response types are derived
     * from the plugin's generated OpenAPI schema, so datasource signature changes surface as
     * compile errors at the call sites.
     */
    useDataApi: () => {
        /**
         * POSTs the (typed) parameter object to a datasource endpoint and resolves with the typed
         * response. The host client handles the Metastore proxy/bypass, branch header and
         * authentication.
         */
        fetch: <P extends DatasourcePath<Paths>>(path: P, parameters: RequestOf<Paths, P>) => Promise<ResponseOf<Paths, P>>;
    };
    /**
     * Fetches a datasource once per distinct (client, path, params) combination.
     * Params are keyed by value (JSON), so inline object literals don't retrigger the fetch.
     */
    useDatasource: <P extends DatasourcePath<Paths>>(path: P, params: RequestOf<Paths, P>) => DatasourceState<ResponseOf<Paths, P>>;
};

/**
 * Creates the typed data-access surface for a plugin from its generated OpenAPI `paths` interface.
 *
 * Call this ONCE at module scope and export the result - calling it inside a component would mint
 * new hook identities on every render:
 *
 * ```ts
 * import type { paths } from "./generated/schema";
 * export const { useDataApi, useDatasource } = createPluginApi<paths>();
 * ```
 */
export function createPluginApi<Paths>(options?: PluginApiOptions): PluginApi<Paths> {
    const pathToEndpoint = options?.pathToEndpoint ?? ((path: string) => path.replace(/^\/api\//, ""));

    const useDataApi = () => {
        const client = useDatasourcesClient();

        return useMemo(() => ({
            fetch: <P extends DatasourcePath<Paths>>(path: P, parameters: RequestOf<Paths, P>): Promise<ResponseOf<Paths, P>> =>
                client.fetchData(pathToEndpoint(path), parameters as Record<string, unknown>),
        }), [client]);
    };

    const useDatasource = <P extends DatasourcePath<Paths>>(path: P, params: RequestOf<Paths, P>): DatasourceState<ResponseOf<Paths, P>> => {
        const dataApi = useDataApi();
        const paramsKey = JSON.stringify(params ?? {});
        const [state, setState] = useState<DatasourceState<ResponseOf<Paths, P>>>({ data: null, loading: true, error: null });

        useEffect(() => {
            let cancelled = false;
            setState(state => ({ ...state, loading: true, error: null }));

            dataApi.fetch(path, JSON.parse(paramsKey))
                .then(data => { if (!cancelled) setState({ data, loading: false, error: null }); })
                .catch(error => { if (!cancelled) setState(state => ({ ...state, loading: false, error })); });

            return () => { cancelled = true; };
            // dataApi identity is stable per (app settings, active branch) - useDatasourcesClient
            // memoizes the client - so including it refetches exactly when a branch switch
            // replaces the client.
        }, [dataApi, path, paramsKey]);

        return state;
    };

    return { useDataApi, useDatasource };
}
